import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DesktopDatePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Icons
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BuildIcon from '@mui/icons-material/Build';

// Services
import propertiesServices from '../../../services/propertiesServices';
import integrationServices from '../../../services/integrationServices';

// Mobile Equipment Card
const MobileEquipmentCard = ({ item, canManageWarranty, isSuperAdmin, onWarranty, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card variant="outlined" sx={{ mb: 2, bgcolor: item.replacedByWarranty ? '#fff3e0' : '#fff', borderColor: item.replacedByWarranty ? '#ff9800' : '#e0e0e0' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.model || 'Unknown Model'}</Typography>
          {item.replacedByWarranty ? (
            <Chip icon={<WarningAmberIcon />} label="Replaced" size="small" color="warning" />
          ) : (
            <Chip label="Active" size="small" color="success" />
          )}
        </Box>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Serial #</Typography>
            <Typography variant="body2">{item.serialNumber || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Install Date</Typography>
            <Typography variant="body2">{item.installDate || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">PO</Typography>
            <Chip label={item.poNumber} size="small" variant="outlined" />
          </Box>
        </Box>
        
        {/* Warranty Replacement Details */}
        {item.replacedByWarranty && item.replacement && (
          <Box sx={{ bgcolor: '#fff8e1', p: 1.5, borderRadius: 1, mb: 1.5 }}>
            <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>REPLACED BY WARRANTY</Typography>
            <Typography variant="body2">New Model: {item.replacement.model || '-'}</Typography>
            <Typography variant="body2">New Serial: {item.replacement.serialNumber || '-'}</Typography>
            <Typography variant="body2">Date: {item.replacement.date || '-'}</Typography>
          </Box>
        )}
        
        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {canManageWarranty && (
            <IconButton size="small" onClick={() => onWarranty(item)} sx={{ bgcolor: '#fff3e0' }}>
              <BuildIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {isSuperAdmin && (
            <>
              <IconButton size="small" onClick={() => onEdit(item)} sx={{ bgcolor: '#e3f2fd' }}>
                <EditIcon sx={{ fontSize: 18, color: '#1976d2' }} />
              </IconButton>
              <IconButton size="small" onClick={() => onDelete(item)} sx={{ bgcolor: '#ffebee' }}>
                <DeleteIcon sx={{ fontSize: 18, color: '#f44336' }} />
              </IconButton>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const PropertyEquipmentTable = ({ equipment = [], propertyId, proposals = [], refresh, userRole = '' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isSuperAdmin = userRole === 'superadmin';
  const isAdmin = userRole === 'admin';
  const canManageWarranty = isSuperAdmin || isAdmin;
  const [items, setItems] = useState(equipment || []);
  const [syncing, setSyncing] = useState(false);
  const [poInput, setPoInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewPo, setPreviewPo] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  
  const [warrantyDialogOpen, setWarrantyDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [replacementData, setReplacementData] = useState({ model: '', serialNumber: '', date: null });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ model: '', serialNumber: '', installDate: null, poNumber: '' });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEquipment, setDeletingEquipment] = useState(null);

  useEffect(() => { setItems(equipment || []); }, [equipment]);

  const toggleRowExpanded = (id) => { setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] })); };

  // Items that the sync will skip (mirror of backend filter)
  const getSkipReason = (whItem) => {
    if (!whItem.serial_number || String(whItem.serial_number).trim() === '') return 'No serial number';
    if (whItem.need_to_return) return 'Need to return';
    if (whItem.returned) return 'Returned';
    if (whItem.damaged) return 'Damaged';
    if (whItem.replaced_by_warranty) return 'Replaced by warranty';
    return null;
  };

  const handleSearchPo = async () => {
    const po = poInput.trim();
    if (!po) { toast.warning('Enter a PO number'); return; }
    setSearching(true);
    try {
      const response = await integrationServices.getWarehouseEquipment(po);
      setPreviewItems(response.data || []);
      setPreviewPo(po);
      setPreviewOpen(true);
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to search Warehouse'); }
    finally { setSearching(false); }
  };

  const handleAddFromWarehouse = async () => {
    setSyncing(true);
    try {
      // If this PO belongs to a CRM proposal, use the proposal-based sync
      // (keeps existing behavior); otherwise sync by raw PO number.
      const matchingProposal = proposals?.find((p) => p.publicId === previewPo);
      const response = matchingProposal
        ? await integrationServices.syncEquipment(matchingProposal.id)
        : await integrationServices.syncEquipmentByPo(propertyId, previewPo);
      if (response.status === 200 || response.status === 201) {
        const { synced = 0, updated = 0 } = response.data || {};
        toast.success(`Equipment added: ${synced} new, ${updated} updated`);
        setPreviewOpen(false);
        if (refresh) refresh();
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to add equipment'); }
    finally { setSyncing(false); }
  };

  const handleOpenWarrantyDialog = (item) => {
    setEditingEquipment(item);
    setReplacementData({ model: item.replacement?.model || '', serialNumber: item.replacement?.serialNumber || '', date: item.replacement?.date ? dayjs(item.replacement.date) : null });
    setWarrantyDialogOpen(true);
  };

  const handleSaveWarrantyReplacement = async () => {
    if (!editingEquipment) return;
    try {
      const updatedEquipment = items.map((item) => {
        if (item.id === editingEquipment.id) {
          return { ...item, replacedByWarranty: true, replacement: { model: replacementData.model, serialNumber: replacementData.serialNumber, date: replacementData.date ? replacementData.date.format('YYYY-MM-DD') : null } };
        }
        return item;
      });
      const response = await propertiesServices.update(propertyId, { equipment: updatedEquipment });
      if (response.status === 200) { toast.success('Warranty replacement saved'); setWarrantyDialogOpen(false); if (refresh) refresh(); }
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleOpenEditDialog = (item) => {
    setEditingEquipment(item);
    setEditData({ model: item.model || '', serialNumber: item.serialNumber || '', installDate: item.installDate ? dayjs(item.installDate) : null, poNumber: item.poNumber || '' });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEquipment) return;
    try {
      const updatedEquipment = items.map((item) => {
        if (item.id === editingEquipment.id) {
          return { ...item, model: editData.model, serialNumber: editData.serialNumber, installDate: editData.installDate ? editData.installDate.format('MM/DD/YYYY') : '', poNumber: editData.poNumber };
        }
        return item;
      });
      const response = await propertiesServices.update(propertyId, { equipment: updatedEquipment });
      if (response.status === 200) { toast.success('Equipment updated'); setEditDialogOpen(false); setEditingEquipment(null); if (refresh) refresh(); }
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleOpenDeleteDialog = (item) => { setDeletingEquipment(item); setDeleteDialogOpen(true); };

  const handleDeleteEquipment = async () => {
    if (!deletingEquipment) return;
    try {
      const updatedEquipment = items.filter((item) => item.id !== deletingEquipment.id);
      const response = await propertiesServices.update(propertyId, { equipment: updatedEquipment });
      if (response.status === 200) { toast.success('Equipment deleted'); setDeleteDialogOpen(false); setDeletingEquipment(null); if (refresh) refresh(); }
    } catch (error) { toast.error('Failed to delete'); }
  };

  const DatePickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Sync Controls */}
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            size="small"
            fullWidth={isMobile}
            sx={{ minWidth: isMobile ? 'auto' : 220 }}
            options={(proposals || []).map((p) => p.publicId)}
            inputValue={poInput}
            onInputChange={(e, newValue) => setPoInput(newValue || '')}
            renderInput={(params) => (
              <TextField {...params} label="PO Number" placeholder="Select or type any PO"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchPo(); } }} />
            )}
          />
          <Button variant="contained" fullWidth={isMobile} startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />} onClick={handleSearchPo} disabled={searching || !poInput.trim()}>
            Search in Warehouse
          </Button>
        </Stack>

        {/* Equipment List */}
        {items.length > 0 ? (
          isMobile ? (
            <Box>
              {items.map((item) => (
                <MobileEquipmentCard key={item.id} item={item} canManageWarranty={canManageWarranty} isSuperAdmin={isSuperAdmin} 
                  onWarranty={handleOpenWarrantyDialog} onEdit={handleOpenEditDialog} onDelete={handleOpenDeleteDialog} />
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="5%"></TableCell>
                    <TableCell width="30%">Model</TableCell>
                    <TableCell width="20%">Serial Number</TableCell>
                    <TableCell width="15%">Install Date</TableCell>
                    <TableCell width="12%">PO</TableCell>
                    <TableCell width="8%">Status</TableCell>
                    <TableCell width="10%">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <Fragment key={item.id}>
                      <TableRow sx={{ backgroundColor: item.replacedByWarranty ? '#fff3e0' : 'inherit' }}>
                        <TableCell>{item.replacedByWarranty && (<IconButton size="small" onClick={() => toggleRowExpanded(item.id)}>{expandedRows[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>)}</TableCell>
                        <TableCell><strong>{item.model || '-'}</strong></TableCell>
                        <TableCell>{item.serialNumber || '-'}</TableCell>
                        <TableCell>{item.installDate || '-'}</TableCell>
                        <TableCell><Chip label={item.poNumber} size="small" variant="outlined" /></TableCell>
                        <TableCell>{item.replacedByWarranty ? <Chip icon={<WarningAmberIcon />} label="Replaced" size="small" color="warning" /> : <Chip label="Active" size="small" color="success" />}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0}>
                            {canManageWarranty && <IconButton size="small" onClick={() => handleOpenWarrantyDialog(item)}><BuildIcon fontSize="small" /></IconButton>}
                            {isSuperAdmin && (<><IconButton size="small" onClick={() => handleOpenEditDialog(item)} color="primary"><EditIcon fontSize="small" /></IconButton><IconButton size="small" onClick={() => handleOpenDeleteDialog(item)} color="error"><DeleteIcon fontSize="small" /></IconButton></>)}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {item.replacedByWarranty && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0 }}>
                            <Collapse in={expandedRows[item.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#fff8e1' }}>
                                <Alert severity="warning" sx={{ mb: 1 }}><strong>REPLACED BY WARRANTY</strong></Alert>
                                <Stack direction="row" spacing={3}>
                                  <Box><Typography variant="caption" color="text.secondary">New Model</Typography><Typography variant="body2">{item.replacement?.model || '-'}</Typography></Box>
                                  <Box><Typography variant="caption" color="text.secondary">New Serial</Typography><Typography variant="body2">{item.replacement?.serialNumber || '-'}</Typography></Box>
                                  <Box><Typography variant="caption" color="text.secondary">Replacement Date</Typography><Typography variant="body2">{item.replacement?.date || '-'}</Typography></Box>
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No equipment installed</Typography>
        )}

        {/* Warehouse PO Search Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle>Warehouse — PO {previewPo}</DialogTitle>
          <DialogContent>
            {previewItems.length === 0 ? (
              <Alert severity="info">No equipment found in Warehouse for this PO.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Model</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Install Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewItems.map((wh, idx) => {
                      const skipReason = getSkipReason(wh);
                      return (
                        <TableRow key={wh.id || idx} sx={{ opacity: skipReason ? 0.5 : 1 }}>
                          <TableCell><strong>{wh.model || '-'}</strong></TableCell>
                          <TableCell>{wh.serial_number || '-'}</TableCell>
                          <TableCell>{wh.install_date || '-'}</TableCell>
                          <TableCell>
                            {skipReason
                              ? <Chip label={`Skipped: ${skipReason}`} size="small" color="default" variant="outlined" />
                              : <Chip label="Will be added" size="small" color="success" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {previewItems.length > 0 && previewItems.every((wh) => getSkipReason(wh)) && (
              <Alert severity="warning" sx={{ mt: 2 }}>All items will be skipped (no serial number / returned / damaged / replaced).</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddFromWarehouse}
              variant="contained"
              startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
              disabled={syncing || previewItems.length === 0 || previewItems.every((wh) => getSkipReason(wh))}
            >
              Add to Installed Equipment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Warranty Replacement Dialog */}
        <Dialog open={warrantyDialogOpen} onClose={() => setWarrantyDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Mark as Warranty Replacement</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Replacement Model" value={replacementData.model} onChange={(e) => setReplacementData({ ...replacementData, model: e.target.value })} fullWidth size="small" />
              <TextField label="Replacement Serial #" value={replacementData.serialNumber} onChange={(e) => setReplacementData({ ...replacementData, serialNumber: e.target.value })} fullWidth size="small" />
              <DatePickerComponent 
                label="Replacement Date" 
                value={replacementData.date} 
                onChange={(newValue) => setReplacementData({ ...replacementData, date: newValue })} 
                inputFormat="MM/DD/YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth />} 
              />
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setWarrantyDialogOpen(false)}>Cancel</Button><Button onClick={handleSaveWarrantyReplacement} variant="contained">Save</Button></DialogActions>
        </Dialog>

        {/* Edit Equipment Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Edit Equipment</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Model" value={editData.model} onChange={(e) => setEditData({ ...editData, model: e.target.value })} fullWidth size="small" />
              <TextField label="Serial Number" value={editData.serialNumber} onChange={(e) => setEditData({ ...editData, serialNumber: e.target.value })} fullWidth size="small" />
              <DatePickerComponent 
                label="Install Date" 
                value={editData.installDate} 
                onChange={(newValue) => setEditData({ ...editData, installDate: newValue })} 
                inputFormat="MM/DD/YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth />} 
              />
              <TextField label="PO Number" value={editData.poNumber} onChange={(e) => setEditData({ ...editData, poNumber: e.target.value })} fullWidth size="small" />
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setEditDialogOpen(false)}>Cancel</Button><Button onClick={handleSaveEdit} variant="contained" color="primary">Save</Button></DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Equipment</DialogTitle>
          <DialogContent><Typography>Delete "{deletingEquipment?.model}"?</Typography></DialogContent>
          <DialogActions><Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button><Button onClick={handleDeleteEquipment} variant="contained" color="error">Delete</Button></DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PropertyEquipmentTable;
